import {
  AssistantPreferences,
  TerminalPairState,
  TERMINAL_ACCESS_LEVELS,
  type AssistantAnswerStyle,
  type AssistantStatus,
  type AssistantTerminalAccess,
  type SetTerminalAccessRequest,
  type TerminalAccessLevel,
  type UpdateAssistantPreferencesRequest,
} from "@cloudserver/contracts";
import { api } from "../api/client.js";
import type { TerminalTab } from "../terminal/store.js";

/*
 * The Autopilot's terminal access levels in the panel (docs/assistant.md,
 * "Terminal access"; owner's design of 2026-10-22): the level of a session,
 * set through `…/terminal/sessions/:sessionId/pair` (`PUT {level}`, `DELETE`
 * for «Выключено»), and the person's defaults through `PUT
 * /assistant/preferences`.
 */

export { TERMINAL_ACCESS_LEVELS };
export type { TerminalAccessLevel };

/** How far a level lets the Autopilot go: «Выключено» 0 … «Авто» 3. */
export const levelRank = (level: TerminalAccessLevel): number =>
  TERMINAL_ACCESS_LEVELS.indexOf(level);

/** No status yet: nothing above «Выключено» is offered. */
const UNKNOWN: AssistantTerminalAccess = {
  default: "off",
  mine: null,
  orgMax: "off",
  sessions: [],
};

export const terminalAccessOf = (status: AssistantStatus | undefined): AssistantTerminalAccess =>
  status?.terminalAccess ?? UNKNOWN;

/**
 * The Autopilot's level in one terminal session now: what the API's frames
 * and the person's last choice put on the tab, else the status's list;
 * «Выключено» for a session the API has not raised.
 */
export function sessionLevel(
  tab: Pick<TerminalTab, "sessionId" | "pair">,
  status: AssistantStatus | undefined,
): TerminalAccessLevel {
  if (tab.pair) return tab.pair.on ? (tab.pair.level ?? "moderated") : "off";
  const listed = terminalAccessOf(status).sessions.find((s) => s.sessionId === tab.sessionId);
  return listed?.level ?? "off";
}

const pairPath = (tab: TerminalTab) =>
  `/projects/${tab.projectId}/servers/${tab.serverId}/terminal/sessions/${tab.sessionId ?? ""}/pair`;

/**
 * Sets a session's level. Raising it above «Выключено» may answer
 * `step_up_required`: the caller's step-up guard asks the second factor and
 * calls again. Returns the API's state.
 */
export async function putSessionLevel(
  tab: TerminalTab,
  level: TerminalAccessLevel,
): Promise<TerminalPairState> {
  if (level === "off") return api(pairPath(tab), { method: "DELETE", schema: TerminalPairState });
  const body: SetTerminalAccessRequest = { level };
  return api(pairPath(tab), { method: "PUT", body, schema: TerminalPairState });
}

/** The person's own style, else the organisation's. */
export const currentStyle = (status: AssistantStatus): AssistantAnswerStyle =>
  status.style.mine ?? status.style.organization;

/**
 * The person's defaults (`PUT /assistant/preferences`): the style and the
 * level of new terminal sessions; a field left out stays, null goes back to
 * the default.
 */
export async function savePreferences(change: UpdateAssistantPreferencesRequest): Promise<void> {
  await api("/assistant/preferences", {
    method: "PUT",
    body: change,
    schema: AssistantPreferences,
  });
}
